import {
  DeviceProvider,
  ToastPortal,
  ToastProvider,
  ViewportProvider,
} from '@treatwell/ui';
import {
  DatadogErrorBoundary,
  captureMessage,
  captureReactError,
  shouldIgnoreError,
} from '@treatwell/ui/datadog';
import {StrictMode} from 'react';
import {createRoot} from 'react-dom/client';
import {Provider} from 'react-redux';
import {Router} from './components/Router';
import {store} from './store';
import {
  getGrowthbook,
  getTranslationsReady,
  initialiseCoreLibraries,
} from './initialise-core-libraries';
import {GrowthBookProvider} from '@growthbook/growthbook-react';
import './styles/app.css';
import {GenericErrorPage} from './Error/GenericErrorPage';
import {BasketErrorMessage} from './components/BasketErrorMessage';
import {whenWafBotControlReady} from './whenWafBotControlReady';
import {setOrderAttributionCookie} from './utils/setOrderAttributionCookie';

const CHECKOUT_UI_ERROR_PARAM = 'basket_error';

whenWafBotControlReady(() => {
  initialiseCoreLibraries();
  setOrderAttributionCookie(location.href);
  renderBasketError();
  renderApp();
});

// Rendered in its own root, decoupled from the main app so the basket error
// banner appears as soon as translations are ready, instead of waiting for the
// full `getGrowthbook()` init chain (deviceId, mParticle, GrowthBook).
async function renderBasketError() {
  const url = new URL(location.href);
  const searchParams = url.searchParams;

  if (!searchParams.has(CHECKOUT_UI_ERROR_PARAM)) return;

  searchParams.delete(CHECKOUT_UI_ERROR_PARAM);
  window.history.replaceState('', '', url.toString());

  await getTranslationsReady();

  const container = document.createElement('div');

  document.body.appendChild(container);
  // Wrapped in the Redux Provider (sharing the main app's store singleton) so
  // the banner can react to the availability request status and dismiss itself
  // on error — see BasketErrorMessage. This only supplies store context and
  // doesn't delay the early render.
  createRoot(container).render(
    <Provider store={store}>
      <BasketErrorMessage />
    </Provider>,
  );
}

async function renderApp() {
  const container = document.getElementById('app');
  const root = createRoot(container!, {
    onUncaughtError: (error: unknown) => {
      if (shouldIgnoreError((error as Error).message, [])) return;
      captureReactError(error as Error, {});
    },
  });
  const growthbook = await getGrowthbook();

  root.render(
    <StrictMode>
      <GrowthBookProvider growthbook={growthbook}>
        <DeviceProvider userAgent={navigator.userAgent}>
          <Provider store={store}>
            <ViewportProvider value={{mobileMax: 520, tabletMax: 699}}>
              <DatadogErrorBoundary
                genericErrorComponent={<GenericErrorPage />}
                handleError={(error: unknown) =>
                  captureMessage((error as Error).message, 'error')
                }
              >
                <ToastProvider>
                  <Router />
                  <ToastPortal root="app" />
                </ToastProvider>
              </DatadogErrorBoundary>
            </ViewportProvider>
          </Provider>
        </DeviceProvider>
      </GrowthBookProvider>
    </StrictMode>,
  );
}
